import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import TextField from '../../../components/SanitizedTextField/SanitizedTextField';
import Backdrop from "@mui/material/Backdrop";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import { useFormik } from "formik";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { useSelector } from "react-redux";
import triangleOff from "../../../images/triangle-off.png";
import triangleOn from "../../../images/triangle-on.png";
import { useSearchParams } from "react-router-dom";
import * as Yup from "yup";
import AppLoader from "../../../components/AppLoader/AppLoader";
import {
  companyName,
  convertDatewithTime,
  getLogo,
  officeName,
} from "../../../services/Functions";
import { fetchComplaintsWithHistory } from "../../../utils/fetchComplaintHistory";
import {
  extractComplaintHistory,
  normalizeComplaintHistoryRow,
  sortComplaintHistoryRows,
} from "../../../utils/complaintHistory";
import eOpsLogo from "../../../images/eops.png";
import "../../../style/common.scss";
import "./ComplaintDetailedHistory.scss";

const SEARCH_CONFIG = {
  placeholder: "Enter 13–18 digit Complaint / CCMS ID",
  hint: "Search using eOps Tracking ID or CCMS Ticket Number.",
};

const formatDateTime = (value) => {
  if (!value) return "-";
  try {
    return convertDatewithTime(value);
  } catch {
    return String(value);
  }
};

const parseComplaintDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatTotalDuration = (startValue, endValue) => {
  const start = parseComplaintDate(startValue);
  const end = parseComplaintDate(endValue);
  if (!start || !end || end < start) return "-";

  let totalMinutes = Math.floor((end.getTime() - start.getTime()) / 60000);
  const days = Math.floor(totalMinutes / (24 * 60));
  totalMinutes %= 24 * 60;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0 || parts.length === 0) parts.push(`${minutes}m`);
  return parts.join(" ");
};

const getStatusClass = (status = "") => {
  const normalized = String(status).toLowerCase().replace(/\s+/g, "");
  if (normalized === "resolved" || normalized === "closed") return "resolved";
  if (normalized === "pending" || normalized === "new") return "pending";
  if (normalized === "allocated") return "allocated";
  if (normalized === "working") return "working";
  if (normalized.includes("hold")) return "onhold";
  return "default";
};

const displayValue = (value) => {
  if (value === undefined || value === null || String(value).trim() === "") {
    return "-";
  }
  return String(value);
};

const digitsOnly = (value) => String(value || "").replace(/\D/g, "");

const isApmsContactorOff = (record) => {
  const status = record?.contactor_status;
  return status === 0 || status === "0" || status === false;
};

const matchApmsLiveRecord = (records, complaint) => {
  const keys = [
    digitsOnly(complaint?.TransformerName),
    digitsOnly(complaint?.ApmsMeterSerialNo),
  ].filter((key) => key.length >= 4);
  const name = String(complaint?.TransformerName || "").trim().toLowerCase();

  return (
    (records || []).find((record) => {
      const msn = digitsOnly(record?.msn);
      if (msn && keys.some((key) => key === msn || key.endsWith(msn) || msn.endsWith(key))) {
        return true;
      }
      const recordName = String(record?.name || "").trim().toLowerCase();
      return Boolean(name && recordName && recordName === name);
    }) || null
  );
};

const formatSourceType = (value) => {
  const code = String(value || "").trim().toUpperCase();
  if (code === "M" || code === "MANUAL") return "Manual";
  if (code === "A" || code === "AUTO") return "Auto";
  return code;
};

const InfoField = ({ label, value }) => (
  <div className="complaint-detailed-history__info-field">
    <span className="complaint-detailed-history__info-label">{label}</span>
    <span className="complaint-detailed-history__info-value">
      {displayValue(value)}
    </span>
  </div>
);

const ComplaintDetailedHistory = ({
  initialComplaintId = "",
  embedded = false,
  onClose = null,
  preloadedComplaint = null,
  preloadedMatched = null,
  preloadedError = "",
  apms = false,
} = {}) => {
  const { token, code: userCode, name: userName, roleName } = useSelector(
    (state) => state.auth,
  );
  const { data: mdmToken } = useSelector((state) => state.authorization);
  const [searchParams] = useSearchParams();
  const [viewBackdrop, setViewBackdrop] = useState(false);
  const [historyNewestFirst, setHistoryNewestFirst] = useState(true);
  const [errorMessage, setErrorMessage] = useState(preloadedError || "");
  const [matchedComplaints, setMatchedComplaints] = useState(
    Array.isArray(preloadedMatched) ? preloadedMatched : [],
  );
  const [resultList, setResultList] = useState(
    Array.isArray(preloadedMatched) ? preloadedMatched : [],
  );
  const [selectedComplaint, setSelectedComplaint] = useState(
    preloadedComplaint || null,
  );
  const [apmsLive, setApmsLive] = useState(null);
  const [apmsLiveOpen, setApmsLiveOpen] = useState(false);
  const [hasSearched, setHasSearched] = useState(
    Boolean(
      preloadedComplaint ||
        (Array.isArray(preloadedMatched) && preloadedMatched.length) ||
        preloadedError,
    ),
  );
  const autoSearchDoneRef = useRef(
    preloadedComplaint ||
      (Array.isArray(preloadedMatched) && preloadedMatched.length) ||
      preloadedError
      ? String(initialComplaintId || "").trim()
      : "",
  );
  const hasPreloadedData = Boolean(
    preloadedComplaint ||
      (Array.isArray(preloadedMatched) && preloadedMatched.length) ||
      preloadedError,
  );

  useEffect(() => {
    if (!apms || !selectedComplaint) {
      setApmsLive(null);
      setApmsLiveOpen(false);
      return undefined;
    }

    const orgCode =
      digitsOnly(selectedComplaint.SubdivisionCode).slice(0, 5) ||
      digitsOnly(selectedComplaint.EOpsTrackingId).slice(0, 5) ||
      digitsOnly(selectedComplaint.CompanyCode).slice(0, 5);
    if (orgCode.length < 2) {
      setApmsLive(null);
      return undefined;
    }

    const privateKey = mdmToken?.privatekey;

    let cancelled = false;
    const loadLiveStatus = async () => {
      const headers = new Headers();
      headers.append("Accept", "application/json");
      if (privateKey) headers.append("privatekey", privateKey);
      const formdata = new FormData();
      formdata.append("organization_code", orgCode);
      const response = await fetch(
        "https://api.mdm.pitc.com.pk/api/get_apms_status",
        { method: "POST", headers, body: formdata },
      );
      const result = await response.json();
      if (cancelled) return;
      const records = Array.isArray(result?.data) ? result.data : [];
      setApmsLive(matchApmsLiveRecord(records, selectedComplaint));
    };

    loadLiveStatus().catch(() => {
      if (!cancelled) setApmsLive(null);
    });

    return () => {
      cancelled = true;
    };
  }, [apms, mdmToken?.privatekey, selectedComplaint]);

  const subDivisions = useMemo(() => {
    try {
      return JSON.parse(window.localStorage.getItem("subDivisions") || "[]");
    } catch {
      return [];
    }
  }, []);

  const historyRows = useMemo(() => {
    if (!selectedComplaint) return [];
    const rawHistory = extractComplaintHistory(selectedComplaint);
    return sortComplaintHistoryRows(
      rawHistory.map((row, index) => normalizeComplaintHistoryRow(row, index)),
    ).map((row, index) => ({ ...row, srNo: index + 1 }));
  }, [selectedComplaint]);

  const visibleHistoryRows = useMemo(() => {
    if (historyNewestFirst) return historyRows;
    return [...historyRows].reverse();
  }, [historyRows, historyNewestFirst]);

  const lastUpdatedAt = useMemo(() => {
    if (!selectedComplaint) return null;
    if (historyRows.length > 0) {
      const latest = historyRows[historyRows.length - 1]?.dateTime;
      if (latest) return latest;
    }
    return (
      selectedComplaint.EndTime ||
      selectedComplaint.StartTime ||
      selectedComplaint.RegisteredAt ||
      null
    );
  }, [selectedComplaint, historyRows]);

  const totalTimeLabel = useMemo(() => {
    if (!selectedComplaint) return "-";
    return formatTotalDuration(
      selectedComplaint.RegisteredAt,
      selectedComplaint.EndTime || lastUpdatedAt,
    );
  }, [selectedComplaint, lastUpdatedAt]);

  const officeLabels = useMemo(() => {
    if (!selectedComplaint) {
      return { company: "-", circle: "-", division: "-", subdivision: "-" };
    }

    const companyCode = selectedComplaint.CompanyCode;
    const circleCode = selectedComplaint.CircleCode;
    const divisionCode = selectedComplaint.DivisionCode;
    const subdivisionCode = selectedComplaint.SubdivisionCode;

    return {
      company:
        (companyCode != null && companyName(String(companyCode))) ||
        displayValue(companyCode),
      circle:
        (circleCode != null && officeName(String(circleCode), subDivisions)) ||
        displayValue(circleCode),
      division:
        (divisionCode != null &&
          officeName(String(divisionCode), subDivisions)) ||
        displayValue(divisionCode),
      subdivision:
        (subdivisionCode != null &&
          officeName(String(subdivisionCode), subDivisions)) ||
        displayValue(subdivisionCode),
    };
  }, [selectedComplaint, subDivisions]);

  const searchFormik = useFormik({
    initialValues: {
      searchValue: "",
    },
    validationSchema: Yup.object({
      searchValue: Yup.string()
        .required("Enter a search value.")
        .min(13, "Complaint ID should be at least 13 digits.")
        .max(18, "Complaint ID should be at most 18 digits."),
    }),
    onSubmit: async (values) => {
      setViewBackdrop(true);
      setErrorMessage("");
      setMatchedComplaints([]);
      setResultList([]);
      setSelectedComplaint(null);
      setHasSearched(true);

      try {
        const { complaints, message } = await fetchComplaintsWithHistory(
          token,
          "complaintId",
          values.searchValue,
        );

        if (complaints.length === 0) {
          setErrorMessage(message);
        } else if (complaints.length === 1) {
          setSelectedComplaint(complaints[0]);
        } else {
          setResultList(complaints);
          setMatchedComplaints(complaints);
        }
      } catch {
        setErrorMessage("Unable to fetch complaint history. Please try again.");
      } finally {
        setViewBackdrop(false);
      }
    },
  });

  useEffect(() => {
    if (hasPreloadedData) return;

    const complaintIdFromQuery = String(
      initialComplaintId ||
        searchParams.get("complaintId") ||
        searchParams.get("id") ||
        "",
    ).trim();
    const searchTypeFromQuery =
      searchParams.get("searchType") === "referenceNo" ? "referenceNo" : "complaintId";
    const autoSearchKey = `${searchTypeFromQuery}:${complaintIdFromQuery}`;

    if (!complaintIdFromQuery || !token) return;
    if (autoSearchDoneRef.current === autoSearchKey) return;

    autoSearchDoneRef.current = autoSearchKey;
    searchFormik.setFieldValue("searchValue", complaintIdFromQuery, false);
    searchFormik.setSubmitting(true);
    setViewBackdrop(true);
    setErrorMessage("");
    setMatchedComplaints([]);
    setResultList([]);
    setSelectedComplaint(null);
    setHasSearched(true);

    (async () => {
      try {
        const { complaints, message } = await fetchComplaintsWithHistory(
          token,
          searchTypeFromQuery,
          complaintIdFromQuery,
        );

        if (complaints.length === 0) {
          setErrorMessage(message);
        } else if (complaints.length === 1) {
          setSelectedComplaint(complaints[0]);
        } else {
          setResultList(complaints);
          setMatchedComplaints(complaints);
        }
      } catch {
        setErrorMessage("Unable to fetch complaint history. Please try again.");
      } finally {
        setViewBackdrop(false);
        searchFormik.setSubmitting(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- auto-run once per complaintId
  }, [searchParams, token, initialComplaintId, hasPreloadedData]);

  const isSearchDisabled =
    !String(searchFormik.values.searchValue || "").trim() ||
    searchFormik.isSubmitting;

  const handleSelectComplaint = (complaint) => {
    setSelectedComplaint(complaint);
    setMatchedComplaints([]);
  };

  const handleBackToList = () => {
    setSelectedComplaint(null);
    setMatchedComplaints(resultList);
  };

  const handleClearResults = () => {
    setSelectedComplaint(null);
    setMatchedComplaints([]);
    setResultList([]);
    setErrorMessage("");
    setHasSearched(false);
    searchFormik.resetForm();
  };

  const handleExportPdf = () => {
    if (!selectedComplaint) return;

    const pdf = new jsPDF("portrait", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const marginX = 12;
    const contentWidth = pageWidth - marginX * 2;
    const companyCode = String(
      selectedComplaint.CompanyCode ||
        String(userCode || "").slice(0, 2) ||
        "14",
    );
    const companyTitle =
      officeLabels.company !== "-"
        ? officeLabels.company
        : companyName(companyCode) || "eOps";

    const drawSectionTitle = (title, yPos, color = [9, 102, 157]) => {
      pdf.setFillColor(color[0], color[1], color[2]);
      pdf.roundedRect(marginX, yPos, contentWidth, 7, 1.2, 1.2, "F");
      pdf.setTextColor(255, 255, 255);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(9);
      pdf.text(title, marginX + 3, yPos + 4.7);
      pdf.setTextColor(30, 41, 59);
      return yPos + 10;
    };

    const ensureSpace = (yPos, needed = 28) => {
      if (yPos + needed < pageHeight - 16) return yPos;
      pdf.addPage();
      return 18;
    };

    // Header: white strip — company logo | company name | eOps logo
    pdf.setFillColor(255, 255, 255);
    pdf.rect(0, 0, pageWidth, 22, "F");

    try {
      const logo = getLogo(companyCode);
      if (logo) {
        const logoW =
          companyCode === "12" ||
          companyCode === "13" ||
          companyCode === "11" ||
          companyCode === "16" ||
          companyCode === "27" ||
          companyCode === "48" ||
          companyCode === "59"
            ? 16
            : 22;
        const logoH =
          companyCode === "12" ||
          companyCode === "13" ||
          companyCode === "11" ||
          companyCode === "16" ||
          companyCode === "27" ||
          companyCode === "48" ||
          companyCode === "59"
            ? 12
            : 8;
        pdf.addImage(logo, "PNG", marginX, (18 - logoH) / 2 + 1, logoW, logoH);
      }
    } catch {
      // optional
    }

    try {
      pdf.addImage(eOpsLogo, "PNG", pageWidth - 30, 5.5, 16, 7);
    } catch {
      // optional
    }

    pdf.setTextColor(15, 23, 42);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(11);
    pdf.text(companyTitle, pageWidth / 2, 10, { align: "center" });
    pdf.setFontSize(9);
    pdf.setTextColor(9, 102, 157);
    pdf.text("Complaint Detailed History", pageWidth / 2, 16, {
      align: "center",
    });

    pdf.setDrawColor(203, 213, 225);
    pdf.setLineWidth(0.4);
    pdf.line(marginX, 20, pageWidth - marginX, 20);

    pdf.setTextColor(100, 116, 139);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(7.5);
    pdf.text(`Generated: ${formatDateTime(new Date())}`, pageWidth / 2, 25, {
      align: "center",
    });

    let y = 30;

    // Ticket strip
    pdf.setFillColor(241, 245, 249);
    pdf.setDrawColor(203, 213, 225);
    pdf.roundedRect(marginX, y, contentWidth, 18, 1.5, 1.5, "FD");

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(8);
    pdf.setTextColor(100, 116, 139);
    pdf.text("eOps ID", marginX + 3, y + 5);
    pdf.text("CCMS ID", marginX + 72, y + 5);
    pdf.text("Status", marginX + 130, y + 5);

    pdf.setTextColor(9, 102, 157);
    pdf.setFontSize(10);
    pdf.text(displayValue(selectedComplaint.EOpsTrackingId), marginX + 3, y + 12);
    pdf.text(
      displayValue(selectedComplaint.TicketNo),
      marginX + 72,
      y + 12,
    );

    const statusText = displayValue(selectedComplaint.Status);
    const statusLower = String(statusText).toLowerCase();
    let statusBg = [226, 232, 240];
    let statusFg = [51, 65, 85];
    if (statusLower.includes("closed") || statusLower.includes("resolved")) {
      statusBg = [220, 252, 231];
      statusFg = [21, 128, 61];
    } else if (statusLower.includes("pending")) {
      statusBg = [255, 228, 230];
      statusFg = [190, 18, 60];
    } else if (statusLower.includes("allocated")) {
      statusBg = [219, 234, 254];
      statusFg = [29, 78, 216];
    }
    const statusWidth = Math.max(22, pdf.getTextWidth(statusText) + 8);
    pdf.setFillColor(statusBg[0], statusBg[1], statusBg[2]);
    pdf.roundedRect(marginX + 130, y + 7, statusWidth, 6.5, 2, 2, "F");
    pdf.setTextColor(statusFg[0], statusFg[1], statusFg[2]);
    pdf.setFontSize(8);
    pdf.text(statusText, marginX + 130 + statusWidth / 2, y + 11.4, {
      align: "center",
    });

    y += 24;

    // Customer Info
    y = drawSectionTitle("Customer Info", y, [14, 165, 233]);
    pdf.autoTable({
      startY: y,
      body: [
        [
          "Name",
          displayValue(selectedComplaint.Name),
          "Mobile",
          displayValue(selectedComplaint.Mobile),
        ],
        [
          "CNIC",
          displayValue(selectedComplaint.CNIC),
          "Feeder",
          displayValue(selectedComplaint.FeederCode),
        ],
        [
          "Nearby",
          displayValue(selectedComplaint.NearByFamousPlace),
          "Address",
          displayValue(selectedComplaint.Address),
        ],
      ],
      theme: "plain",
      styles: {
        fontSize: 8,
        cellPadding: { top: 2, bottom: 2, left: 2, right: 2 },
        textColor: [30, 41, 59],
        lineColor: [226, 232, 240],
        lineWidth: 0.2,
        valign: "middle",
      },
      columnStyles: {
        0: {
          cellWidth: 24,
          fontStyle: "bold",
          fillColor: [248, 250, 252],
          textColor: [100, 116, 139],
        },
        1: { cellWidth: 66 },
        2: {
          cellWidth: 24,
          fontStyle: "bold",
          fillColor: [248, 250, 252],
          textColor: [100, 116, 139],
        },
        3: { cellWidth: 66 },
      },
      margin: { left: marginX, right: marginX },
    });
    y = pdf.lastAutoTable.finalY + 6;

    // Unit Info
    y = ensureSpace(y, 40);
    y = drawSectionTitle("Unit Info", y, [34, 197, 94]);
    pdf.autoTable({
      startY: y,
      body: [
        [
          "Province",
          displayValue(selectedComplaint.Province),
          "Company",
          displayValue(officeLabels.company),
        ],
        [
          "Circle",
          displayValue(officeLabels.circle),
          "Division",
          displayValue(officeLabels.division),
        ],
        [
          "Subdivision",
          displayValue(officeLabels.subdivision),
          "Assigned To",
          displayValue(selectedComplaint.UserFullName),
        ],
      ],
      theme: "plain",
      styles: {
        fontSize: 8,
        cellPadding: { top: 2, bottom: 2, left: 2, right: 2 },
        textColor: [30, 41, 59],
        lineColor: [226, 232, 240],
        lineWidth: 0.2,
        valign: "middle",
      },
      columnStyles: {
        0: {
          cellWidth: 24,
          fontStyle: "bold",
          fillColor: [248, 250, 252],
          textColor: [100, 116, 139],
        },
        1: { cellWidth: 66 },
        2: {
          cellWidth: 24,
          fontStyle: "bold",
          fillColor: [248, 250, 252],
          textColor: [100, 116, 139],
        },
        3: { cellWidth: 66 },
      },
      margin: { left: marginX, right: marginX },
    });
    y = pdf.lastAutoTable.finalY + 6;

    // Status & Timings
    y = ensureSpace(y, 42);
    y = drawSectionTitle("Status & Timings", y, [37, 99, 235]);
    pdf.autoTable({
      startY: y,
      body: [
        ...(apms
          ? [[
              "Category",
              displayValue(selectedComplaint.ComplaintCategory),
              "Nature",
              displayValue(selectedComplaint.ComplaintType),
            ]]
          : []),
        [
          "Nature",
          displayValue(selectedComplaint.ComplaintType),
          "Source",
          displayValue(
            String(selectedComplaint.Source || "").replace(/_/g, " "),
          ),
        ],
        [
          "Registered at",
          formatDateTime(selectedComplaint.RegisteredAt),
          "Start Time",
          formatDateTime(selectedComplaint.StartTime),
        ],
        [
          "Last updated",
          formatDateTime(
            historyRows.length > 0
              ? historyRows[historyRows.length - 1]?.dateTime
              : selectedComplaint.EndTime || selectedComplaint.StartTime,
          ),
          "Resolution Time",
          formatDateTime(selectedComplaint.EndTime),
        ],
        [
          "Time taken to resolved",
          formatTotalDuration(
            selectedComplaint.RegisteredAt,
            selectedComplaint.EndTime ||
              (historyRows.length > 0
                ? historyRows[historyRows.length - 1]?.dateTime
                : selectedComplaint.StartTime),
          ),
          "Status",
          displayValue(selectedComplaint.Status),
        ],
      ],
      theme: "plain",
      styles: {
        fontSize: 8,
        cellPadding: { top: 2, bottom: 2, left: 2, right: 2 },
        textColor: [30, 41, 59],
        lineColor: [226, 232, 240],
        lineWidth: 0.2,
        valign: "middle",
      },
      columnStyles: {
        0: {
          cellWidth: 28,
          fontStyle: "bold",
          fillColor: [248, 250, 252],
          textColor: [100, 116, 139],
        },
        1: { cellWidth: 62 },
        2: {
          cellWidth: 28,
          fontStyle: "bold",
          fillColor: [248, 250, 252],
          textColor: [100, 116, 139],
        },
        3: { cellWidth: 62 },
      },
      margin: { left: marginX, right: marginX },
    });
    y = pdf.lastAutoTable.finalY + 6;

    // Description / Remarks cards
    const description = displayValue(selectedComplaint.ComplaintDescription);
    const remarks = displayValue(
      selectedComplaint.LinemanRemarks || selectedComplaint.Remarks,
    );

    y = ensureSpace(y, 28);
    pdf.setFillColor(248, 250, 252);
    pdf.setDrawColor(226, 232, 240);
    pdf.roundedRect(marginX, y, contentWidth, 16, 1.2, 1.2, "FD");
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(8);
    pdf.setTextColor(100, 116, 139);
    pdf.text("Description", marginX + 3, y + 5);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(30, 41, 59);
    const descLines = pdf.splitTextToSize(description, contentWidth - 6);
    pdf.text(descLines.slice(0, 2), marginX + 3, y + 10);
    y += 20;

    y = ensureSpace(y, 28);
    pdf.setFillColor(248, 250, 252);
    pdf.roundedRect(marginX, y, contentWidth, 16, 1.2, 1.2, "FD");
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(8);
    pdf.setTextColor(100, 116, 139);
    pdf.text("Latest Remarks", marginX + 3, y + 5);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(30, 41, 59);
    const remarkLines = pdf.splitTextToSize(remarks, contentWidth - 6);
    pdf.text(remarkLines.slice(0, 2), marginX + 3, y + 10);
    y += 20;

    // History
    y = ensureSpace(y, 40);
    y = drawSectionTitle("Complaint History", y, [0, 57, 113]);

    const historyBody =
      historyRows.length > 0
        ? historyRows.map((row) => [
            String(row.srNo),
            displayValue(row.status),
            displayValue(row.processedBy),
            displayValue(row.assignedTo),
            formatDateTime(row.dateTime),
            displayValue(row.remarks),
          ])
        : [["-", "No history records found", "-", "-", "-", "-"]];

    pdf.autoTable({
      startY: y,
      head: [
        ["#", "Status", "Processed By", "Assigned To", "Date & Time", "Remarks"],
      ],
      body: historyBody,
      theme: "striped",
      styles: {
        fontSize: 7.5,
        cellPadding: 2,
        textColor: [30, 41, 59],
        lineColor: [226, 232, 240],
        lineWidth: 0.15,
        valign: "middle",
      },
      headStyles: {
        fillColor: [15, 23, 42],
        textColor: 255,
        fontStyle: "bold",
        fontSize: 7.5,
      },
      alternateRowStyles: {
        fillColor: [248, 250, 252],
      },
      columnStyles: {
        0: { cellWidth: 10, halign: "center" },
        1: { cellWidth: 24 },
        2: { cellWidth: 28 },
        3: { cellWidth: 28 },
        4: { cellWidth: 32 },
        5: { cellWidth: "auto" },
      },
      margin: { left: marginX, right: marginX },
    });

    const pageCount = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i += 1) {
      pdf.setPage(i);
      pdf.setFontSize(7);
      pdf.setTextColor(148, 163, 184);
      pdf.setFont("helvetica", "normal");
      pdf.setDrawColor(226, 232, 240);
      pdf.line(marginX, pageHeight - 12, pageWidth - marginX, pageHeight - 12);
      pdf.text(`Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 8, {
        align: "center",
      });
      if (userName) {
        pdf.text(
          `Exported by ${userName}${roleName ? ` · ${roleName}` : ""}`,
          marginX,
          pageHeight - 8,
        );
      }
    }

    const fileId =
      selectedComplaint.EOpsTrackingId ||
      selectedComplaint.TicketNo ||
      "complaint";
    pdf.save(`Complaint_History_${fileId}.pdf`);
  };

  return (
    <Box
      className={`complaint-detailed-history${
        embedded ? " complaint-detailed-history--embedded" : ""
      }`}
    >
      <Backdrop open={viewBackdrop} sx={{ color: "#fff", zIndex: 14000 }}>
        <AppLoader />
      </Backdrop>

      {!embedded ? (
        <Paper className="complaint-detailed-history__hero" elevation={0}>
          <div className="complaint-detailed-history__hero-top">
            <div className="complaint-detailed-history__hero-title-wrap">
              <div className="complaint-detailed-history__hero-icon">
                <SearchOutlinedIcon />
              </div>
              <div>
                <Typography variant="h6" component="h1">
                  Complaint Detailed History
                </Typography>
                <Typography component="p">
                  Track complete complaint journey from registration to
                  resolution.
                </Typography>
              </div>
            </div>
          </div>

          <div className="complaint-detailed-history__hero-body">
            <form onSubmit={searchFormik.handleSubmit}>
              <div className="complaint-detailed-history__search-row">
                <TextField
                  fullWidth
                  name="searchValue"
                  value={searchFormik.values.searchValue}
                  onChange={searchFormik.handleChange}
                  onBlur={searchFormik.handleBlur}
                  className="complaint-detailed-history__search-field"
                  autoComplete="off"
                  placeholder={SEARCH_CONFIG.placeholder}
                  inputProps={{
                    maxLength: 18,
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchOutlinedIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  className="complaint-detailed-history__search-btn"
                  startIcon={<SearchOutlinedIcon />}
                  disabled={isSearchDisabled}
                >
                  Search
                </Button>
              </div>

              <Typography className="complaint-detailed-history__hint">
                {SEARCH_CONFIG.hint}
              </Typography>

              {searchFormik.errors.searchValue &&
              searchFormik.touched.searchValue ? (
                <Typography className="complaint-detailed-history__error">
                  {searchFormik.errors.searchValue}
                </Typography>
              ) : null}

              {errorMessage ? (
                <Typography className="complaint-detailed-history__error">
                  {errorMessage}
                </Typography>
              ) : null}
            </form>
          </div>
        </Paper>
      ) : (
        <div className="complaint-detailed-history__popup-bar">
          <div className="complaint-detailed-history__popup-bar-title">
            <HistoryOutlinedIcon fontSize="small" />
            <Typography component="h2">
              {apms ? "APMS Complaint Detailed History" : "Complaint Detailed History"}
            </Typography>
          </div>
          <div className="complaint-detailed-history__popup-bar-actions">
            {apms && apmsLive ? (
              <button
                type="button"
                className={`complaint-detailed-history__apms-status complaint-detailed-history__apms-status--${
                  isApmsContactorOff(apmsLive) ? "off" : "on"
                }`}
                onClick={() => setApmsLiveOpen(true)}
              >
                <img
                  src={isApmsContactorOff(apmsLive) ? triangleOff : triangleOn}
                  alt=""
                />
                {isApmsContactorOff(apmsLive) ? "OFF" : "ON"}
              </button>
            ) : null}
            {typeof onClose === "function" ? (
              <Button
                type="button"
                variant="outlined"
                size="small"
                className="complaint-detailed-history__popup-close"
                startIcon={<CloseOutlinedIcon />}
                onClick={onClose}
              >
                Close
              </Button>
            ) : null}
          </div>
        </div>
      )}

      {embedded && errorMessage && !selectedComplaint ? (
        <div className="complaint-detailed-history__empty">
          <HistoryOutlinedIcon />
          <h2>Unable to load history</h2>
          <p>{errorMessage}</p>
        </div>
      ) : null}

      {!embedded && !hasSearched && !selectedComplaint ? (
        <div className="complaint-detailed-history__empty">
          <HistoryOutlinedIcon />
          <h2>Search a complaint to view its history</h2>
          <p>
            Use Complaint ID to load complaint details and step-by-step
            processing history.
          </p>
        </div>
      ) : null}

      {matchedComplaints.length > 0 ? (
        <Paper className="complaint-detailed-history__panel" elevation={0}>
          <div className="complaint-detailed-history__panel-head">
            <Typography className="complaint-detailed-history__panel-title">
              Multiple complaints found — select one
            </Typography>
          </div>
          <TableContainer className="complaint-detailed-history__table-wrap">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Complaint ID</TableCell>
                  <TableCell>Ref No.</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Mobile</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell className="complaint-detailed-history__action-cell" align="right">
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {matchedComplaints.map((complaint, index) => (
                  <TableRow key={`${complaint.EOpsTrackingId}-${index}`}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{complaint.EOpsTrackingId || "-"}</TableCell>
                    <TableCell>{complaint.RefNo || "-"}</TableCell>
                    <TableCell>{complaint.Name || "-"}</TableCell>
                    <TableCell>{complaint.Mobile || "-"}</TableCell>
                    <TableCell>{complaint.ComplaintType || "-"}</TableCell>
                    <TableCell>
                      <span
                        className={`complaint-detailed-history__status-badge complaint-detailed-history__status-badge--${getStatusClass(
                          complaint.Status,
                        )}`}
                      >
                        {complaint.Status || "-"}
                      </span>
                    </TableCell>
                    <TableCell className="complaint-detailed-history__action-cell" align="right">
                      <Button
                        size="small"
                        variant="contained"
                        className="complaint-detailed-history__view-btn"
                        onClick={() => handleSelectComplaint(complaint)}
                      >
                        View History
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ) : null}

      {selectedComplaint ? (
        <Paper className="complaint-detailed-history__ticket" elevation={0}>
          <div className="complaint-detailed-history__ticket-head">
            <div>
              <p className="complaint-detailed-history__breadcrumb">Complaints</p>
              <div className="complaint-detailed-history__ticket-ids">
                <h2 className="complaint-detailed-history__ticket-title">
                  eOps ID:{" "}
                  <span className="complaint-detailed-history__ticket-id">
                    {displayValue(selectedComplaint.EOpsTrackingId)}
                  </span>
                </h2>
                <h2 className="complaint-detailed-history__ticket-title">
                  CCMS ID:{" "}
                  <span className="complaint-detailed-history__ticket-id">
                    {displayValue(selectedComplaint.TicketNo)}
                  </span>
                  <span
                    className={`complaint-detailed-history__status-badge complaint-detailed-history__status-badge--${getStatusClass(
                      selectedComplaint.Status,
                    )}`}
                  >
                    {displayValue(selectedComplaint.Status)}
                  </span>
                </h2>
              </div>
            </div>
            <div className="complaint-detailed-history__ticket-actions">
              {resultList.length > 1 ? (
                <Button
                  variant="outlined"
                  size="small"
                  className="complaint-detailed-history__list-back"
                  startIcon={<ArrowBackOutlinedIcon />}
                  onClick={handleBackToList}
                >
                  Back
                </Button>
              ) : null}
              <Button
                variant="contained"
                size="small"
                className="complaint-detailed-history__export-btn"
                startIcon={<PictureAsPdfOutlinedIcon />}
                onClick={handleExportPdf}
              >
                Export PDF
              </Button>
              {!embedded ? (
                <Button
                  variant="outlined"
                  size="small"
                  className="complaint-detailed-history__clear-btn"
                  onClick={handleClearResults}
                >
                  New Search
                </Button>
              ) : null}
            </div>
          </div>

          <div
            className={`complaint-detailed-history__info-cards${
              apms ? " complaint-detailed-history__info-cards--apms" : ""
            }`}
          >
            <section className="complaint-detailed-history__info-card complaint-detailed-history__info-card--customer">
              <header>Customer Info</header>
              <div className="complaint-detailed-history__info-grid">
                {apms ? (
                  <>
                    <InfoField
                      label="Transformer (ref-No)"
                      value={selectedComplaint.TransformerName || selectedComplaint.Name}
                    />
                    <InfoField
                      label="Feeder"
                      value={selectedComplaint.FeederCode}
                    />
                    <InfoField
                      label="Category"
                      value={selectedComplaint.ComplaintCategory}
                    />
                    <InfoField
                      label="Nature"
                      value={selectedComplaint.ComplaintType}
                    />
                    <InfoField
                      label="Source"
                      value={String(selectedComplaint.Source || "").replace(/_/g, " ")}
                    />
                    <InfoField
                      label="Source Type"
                      value={formatSourceType(selectedComplaint.SourceType)}
                    />
                  </>
                ) : (
                  <>
                    <InfoField label="Name" value={selectedComplaint.Name} />
                    <InfoField label="Mobile" value={selectedComplaint.Mobile} />
                    <InfoField label="CNIC" value={selectedComplaint.CNIC} />
                    <InfoField label="Address" value={selectedComplaint.Address} />
                    <InfoField
                      label="Nearby"
                      value={selectedComplaint.NearByFamousPlace}
                    />
                    <InfoField
                      label="Feeder"
                      value={selectedComplaint.FeederCode}
                    />
                  </>
                )}
              </div>
            </section>

            <section className="complaint-detailed-history__info-card complaint-detailed-history__info-card--unit">
              <header>Unit Info</header>
              <div className="complaint-detailed-history__info-grid">
                {apms ? null : (
                  <InfoField
                    label="Province"
                    value={selectedComplaint.Province}
                  />
                )}
                <InfoField label="Company" value={officeLabels.company} />
                <InfoField label="Circle" value={officeLabels.circle} />
                <InfoField label="Division" value={officeLabels.division} />
                <InfoField
                  label="Subdivision"
                  value={officeLabels.subdivision}
                />
                <InfoField
                  label="Assigned To"
                  value={selectedComplaint.UserFullName}
                />
              </div>
            </section>
          </div>

          <div
            className={`complaint-detailed-history__meta-panel${
              apms ? " complaint-detailed-history__meta-panel--apms" : ""
            }`}
          >
            <div className="complaint-detailed-history__meta-grid">
              {apms ? (
                <div className="complaint-detailed-history__meta-item">
                  <span className="complaint-detailed-history__meta-label">
                    Registered By
                  </span>
                  <span className="complaint-detailed-history__meta-value">
                    {displayValue(selectedComplaint.RegisteredBy)}
                  </span>
                </div>
              ) : null}
              <div className="complaint-detailed-history__meta-item">
                <span className="complaint-detailed-history__meta-label">
                  Registered at
                </span>
                <span className="complaint-detailed-history__meta-value complaint-detailed-history__meta-value--registered">
                  {formatDateTime(selectedComplaint.RegisteredAt)}
                </span>
              </div>
              <div className="complaint-detailed-history__meta-item">
                <span className="complaint-detailed-history__meta-label">
                  Start Time
                </span>
                <span className="complaint-detailed-history__meta-value complaint-detailed-history__meta-value--start">
                  {formatDateTime(selectedComplaint.StartTime)}
                </span>
              </div>
              <div className="complaint-detailed-history__meta-item">
                <span className="complaint-detailed-history__meta-label">
                  Last updated
                </span>
                <span className="complaint-detailed-history__meta-value complaint-detailed-history__meta-value--review">
                  {formatDateTime(lastUpdatedAt)}
                </span>
              </div>
              <div className="complaint-detailed-history__meta-item">
                <span className="complaint-detailed-history__meta-label">
                  Resolution Time
                </span>
                <span className="complaint-detailed-history__meta-value complaint-detailed-history__meta-value--resolved">
                  {formatDateTime(selectedComplaint.EndTime)}
                </span>
              </div>
              <div className="complaint-detailed-history__meta-item">
                <span className="complaint-detailed-history__meta-label">
                  Time taken to resolved
                </span>
                <span className="complaint-detailed-history__meta-value complaint-detailed-history__meta-value--total">
                  {totalTimeLabel}
                </span>
              </div>
              <div className="complaint-detailed-history__meta-item">
                <span className="complaint-detailed-history__meta-label">
                  Status
                </span>
                <span
                  className={`complaint-detailed-history__status-badge complaint-detailed-history__status-badge--${getStatusClass(
                    selectedComplaint.Status,
                  )}`}
                >
                  {displayValue(selectedComplaint.Status)}
                </span>
              </div>
              {apms ? null : (
                <>
                  <div className="complaint-detailed-history__meta-item">
                    <span className="complaint-detailed-history__meta-label">
                      Nature
                    </span>
                    <span className="complaint-detailed-history__meta-value">
                      {displayValue(selectedComplaint.ComplaintType)}
                    </span>
                  </div>
                  <div className="complaint-detailed-history__meta-item">
                    <span className="complaint-detailed-history__meta-label">
                      Source
                    </span>
                    <span className="complaint-detailed-history__meta-value">
                      {displayValue(
                        String(selectedComplaint.Source || "").replace(/_/g, " "),
                      )}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="complaint-detailed-history__description">
            <span className="complaint-detailed-history__meta-label">
              Description
            </span>
            <p>{displayValue(selectedComplaint.ComplaintDescription)}</p>
          </div>

          {selectedComplaint.LinemanRemarks || selectedComplaint.Remarks ? (
            <div className="complaint-detailed-history__description complaint-detailed-history__description--muted">
              <div className="complaint-detailed-history__remarks-head">
                <span className="complaint-detailed-history__meta-label">
                  Latest Remarks
                </span>
                {selectedComplaint.ModificationDateTime ? (
                  <span className="complaint-detailed-history__remarks-date">
                    {formatDateTime(selectedComplaint.ModificationDateTime)}
                  </span>
                ) : null}
              </div>
              <p>
                {displayValue(
                  selectedComplaint.LinemanRemarks || selectedComplaint.Remarks,
                )}
              </p>
            </div>
          ) : null}

          <div className="complaint-detailed-history__history-panel">
            <div className="complaint-detailed-history__history-panel-head">
              <HistoryOutlinedIcon />
              <h3>History</h3>
              {historyRows.length > 1 ? (
                <div className="complaint-detailed-history__history-order" role="group" aria-label="History order">
                  <button
                    type="button"
                    className={!historyNewestFirst ? "is-active" : ""}
                    onClick={() => setHistoryNewestFirst(false)}
                  >
                    Top to bottom
                  </button>
                  <button
                    type="button"
                    className={historyNewestFirst ? "is-active" : ""}
                    onClick={() => setHistoryNewestFirst(true)}
                  >
                    Bottom to top
                  </button>
                </div>
              ) : null}
            </div>

            {visibleHistoryRows.length > 0 ? (
              <ul className="complaint-detailed-history__timeline">
                {visibleHistoryRows.map((row) => (
                  <li
                    key={`${row.srNo}-${row.dateTime}-${row.status}`}
                    className="complaint-detailed-history__timeline-item"
                  >
                    <div className="complaint-detailed-history__timeline-time">
                      <AccessTimeOutlinedIcon />
                      <span>{formatDateTime(row.dateTime)}</span>
                    </div>
                    <p className="complaint-detailed-history__timeline-action">
                      {row.actionLabel || (
                        <>
                          {row.processedBy} changed status to{" "}
                          <strong>{row.status}</strong>
                          {row.remarks && row.remarks !== "-"
                            ? ` [${row.remarks}]`
                            : ""}
                        </>
                      )}
                    </p>
                    {row.assignedTo && row.assignedTo !== "-" ? (
                      <p className="complaint-detailed-history__timeline-meta">
                        Assigned to: {row.assignedTo}
                      </p>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="complaint-detailed-history__history-empty">
                No history records found for this complaint.
              </p>
            )}
          </div>
        </Paper>
      ) : null}

      <Dialog
        open={apmsLiveOpen && Boolean(apmsLive)}
        onClose={() => setApmsLiveOpen(false)}
        maxWidth="xs"
        fullWidth
        className="complaint-detailed-history__apms-live"
      >
        <DialogTitle className="complaint-detailed-history__apms-live-title">
          <span>APMS Status</span>
          <Button
            type="button"
            size="small"
            onClick={() => setApmsLiveOpen(false)}
          >
            Close
          </Button>
        </DialogTitle>
        <DialogContent>
          {apmsLive ? (
            <div className="complaint-detailed-history__apms-live-grid">
              <span>Location</span>
              <strong>{displayValue(apmsLive.name)}</strong>
              <span>MSN</span>
              <strong>{displayValue(apmsLive.msn)}</strong>
              <span>Load</span>
              <strong>{displayValue(apmsLive.kva)} kVA</strong>
              <span>Capacity</span>
              <strong>{displayValue(apmsLive.apms_capacity)} kVA</strong>
              <span>Voltage</span>
              <strong>
                {displayValue(
                  [apmsLive.v1, apmsLive.v2, apmsLive.v3].filter(Boolean).join(" / "),
                )}
              </strong>
              <span>Current</span>
              <strong>
                {displayValue(
                  [apmsLive.i1, apmsLive.i2, apmsLive.i3].filter(Boolean).join(" / "),
                )}
              </strong>
              <span>Last Communication</span>
              <strong>{displayValue(apmsLive.last_communication_time)}</strong>
              <span>Status</span>
              <strong
                className={
                  isApmsContactorOff(apmsLive)
                    ? "complaint-detailed-history__apms-live-off"
                    : "complaint-detailed-history__apms-live-on"
                }
              >
                {isApmsContactorOff(apmsLive) ? "OFF" : "ON"}
              </strong>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ComplaintDetailedHistory;
